import React, { useEffect } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Helmet } from 'react-helmet-async';

function TermsOfService() {
  useEffect(() => {
    window.scrollTo(0, 0);
    AOS.init({ duration: 1000 });
  }, []);

  return (
    <div className="terms-page">
      <Helmet>
        <title>Terms of Service | AzyByte Technologies</title>
        <meta
          name="description"
          content="Read the terms and conditions governing the use of software engineering, consultancy services, and digital products provided by AzyByte Technologies."
        />
        <link rel="canonical" href="https://www.azybyte.in/terms" />

        {/* Open Graph Tags */}
        <meta property="og:title" content="Terms of Service | AzyByte Technologies" />
        <meta property="og:description" content="The terms and legal conditions for engaging with software and services from AzyByte Technologies." />
        <meta property="og:url" content="https://www.azybyte.in/terms" />
        <meta property="og:type" content="website" />
      </Helmet>
      <Navbar />


      {/* Header Section */}
      <section className="bg-light text-dark py-5 mt-5">
        <div className="container pt-5 pb-4 text-center">
          <h1 className="fw-bold display-4 text-primary" data-aos="zoom-in">Terms of Service</h1>
          <p className="text-secondary" data-aos="fade-up">Last Updated: April 25, 2026</p>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-5 bg-white">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 shadow-sm p-4 p-md-5 rounded-4 border bg-white" data-aos="fade-up">

              {/* Acceptance */}
              <div className="mb-5">
                <div className="d-flex align-items-center mb-3">
                    <span className="badge bg-primary-subtle text-primary me-2 px-3 py-2">01</span>
                    <h4 className="fw-bold m-0">Acceptance of Terms</h4>
                </div>
                <p className="text-muted ms-md-5">
                  By accessing the AzyByte Technologies website or engaging our services, you agree to be bound by these Terms of Service. If you do not agree to all the terms and conditions, you may not access our website or use our services.
                </p>
              </div>

              {/* Scope */}
              <div className="mb-5">
                <div className="d-flex align-items-center mb-3">
                    <span className="badge bg-primary-subtle text-primary me-2 px-3 py-2">02</span>
                    <h4 className="fw-bold m-0">Scope of Services</h4>
                </div>
                <p className="text-muted ms-md-5">
                  AzyByte Technologies provides custom software development, web development, process automation (including WhatsApp bots), and IT consultancy. The specific deliverables, timelines, and technical requirements will be outlined in a mutually agreed-upon project proposal or invoice.
                </p>
              </div>

              {/* Payment Terms - Highlighted */}
              <div className="mb-5 p-4 bg-light rounded-4 border-start border-primary border-4 ms-md-5">
                <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-credit-card text-primary fs-4 me-2"></i>
                    <h4 className="fw-bold m-0">3. Payment Terms & Billing</h4>
                </div>
                <ul className="list-unstyled text-muted">
                  <li className="mb-2"><strong>Advance Payment:</strong> A standard, non-refundable advance of 40% is required before any design or development work begins.</li>
                  <li className="mb-2"><strong>Milestones:</strong> The remaining balance will be billed according to project milestones or upon completion.</li>
                  <li className="mb-0"><strong>Delivery:</strong> Source code and final deployment will only be transferred upon 100% clearance of the invoice amount.</li>
                </ul>
              </div>

              {/* Third Party */}
              <div className="mb-5">
                <div className="d-flex align-items-center mb-3">
                    <span className="badge bg-primary-subtle text-primary me-2 px-3 py-2">04</span>
                    <h4 className="fw-bold m-0">Third-Party Dependencies & Uptime</h4>
                </div>
                <p className="text-muted ms-md-5">
                  Our solutions rely on third-party platforms (e.g., Meta APIs, AWS, payment gateways). AzyByte Technologies is not liable for system outages, API changes, or data losses caused directly by these providers.
                </p>
              </div>

              {/* IP Rights */}
              <div className="mb-5">
                <div className="d-flex align-items-center mb-3">
                    <span className="badge bg-primary-subtle text-primary me-2 px-3 py-2">05</span>
                    <h4 className="fw-bold m-0">Intellectual Property Rights</h4>
                </div>
                <p className="text-muted ms-md-5">
                  Upon final payment, clients receive full usage rights of the customized final product. AzyByte Technologies retains rights to underlying open-source components and generalized code logic developed during the project.
                </p>
              </div>

              {/* Liability */}
              <div className="mb-5">
                <div className="d-flex align-items-center mb-3">
                    <span className="badge bg-primary-subtle text-primary me-2 px-3 py-2">06</span>
                    <h4 className="fw-bold m-0">Limitation of Liability</h4>
                </div>
                <p className="text-muted ms-md-5">
                  AzyByte Technologies and its contractors shall not be held liable for indirect or incidental damages, including loss of profits or business interruptions arising from the use of our delivered software.
                </p>
              </div>

              {/* Scope Creep */}
              <div className="mb-5">
                <div className="d-flex align-items-center mb-3">
                    <span className="badge bg-primary-subtle text-primary me-2 px-3 py-2">07</span>
                    <h4 className="fw-bold m-0">Revisions and Scope Creep</h4>
                </div>
                <p className="text-muted ms-md-5">
                  Fees are based on the initial scope. Additional features or structural changes requested after commencement will be treated as "scope creep" and subject to additional billing.
                </p>
              </div>

              {/* Governing Law */}
              <div className="mb-5">
                <div className="d-flex align-items-center mb-3">
                    <span className="badge bg-primary-subtle text-primary me-2 px-3 py-2">08</span>
                    <h4 className="fw-bold m-0">Governing Law & Jurisdiction</h4>
                </div>
                <p className="text-muted ms-md-5">
                  These Terms are governed by the laws of India. Disputes shall be subject to the exclusive jurisdiction of the courts in <strong>Gujarat, India</strong>.
                </p>
              </div>

              {/* Contact */}
              <div className="bg-primary bg-opacity-10 p-4 rounded-4 text-center">
                <h5 className="fw-bold">Questions?</h5>
                <p className="text-muted mb-3">Contact us for clarification on any of our terms.</p>
                <a href="mailto:contact@azybyte.in" className="btn btn-primary rounded-pill px-4">contact@azybyte.in</a>
              </div>

            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default TermsOfService;